import { useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@multaqa/ui/components/ui/dialog'
import { Input } from '@multaqa/ui/components/ui/input'
import { Label } from '@multaqa/ui/components/ui/label'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { useState } from 'react'
import { formatErrorMessage } from '@/lib/error-message'
import type { TemplateActions, TemplateItem, TemplateKind } from './group-defaults-templates'

export function TemplateDialog({
  kind,
  item,
  actions,
  onClose,
}: {
  kind: TemplateKind
  item: TemplateItem | null
  actions: TemplateActions
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [nameEn, setNameEn] = useState(item?.name.en ?? '')
  const [nameAr, setNameAr] = useState(item?.name.ar ?? '')
  const [code, setCode] = useState(item?.code ?? '')
  const [formError, setFormError] = useState<string | null>(null)
  const save = async () => {
    if (!nameEn.trim() || !nameAr.trim()) {
      setFormError(t('groups.defaults.templates.form.nameRequired'))
      return
    }
    if (!code.trim()) {
      setFormError(t('groups.defaults.templates.form.codeRequired'))
      return
    }
    const input = {
      name: { en: nameEn.trim(), ar: nameAr.trim() },
      code: code.trim().toUpperCase(),
    }
    try {
      if (item) await actions.update(item.id, input)
      else await actions.create(input)
      onClose()
      toast.success(t('groups.defaults.templates.saved'))
    } catch (cause) {
      setFormError(errorMessage(cause, t))
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !actions.pending) onClose()
      }}
    >
      <DialogContent
        closeLabel={t('groups.defaults.templates.form.cancel')}
        className="max-w-lg"
        data-testid={`group-template-${kind}-dialog`}
      >
        <DialogHeader>
          <DialogTitle>
            {t(
              item
                ? 'groups.defaults.templates.form.editTitle'
                : 'groups.defaults.templates.form.createTitle',
            )}
          </DialogTitle>
          <DialogDescription>
            {t(`groups.defaults.templates.${kind}.description`)}
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            void save()
          }}
          noValidate
        >
          <DialogBody className="grid gap-4">
            {formError ? (
              <p className="text-small text-danger" role="alert">
                {formError}
              </p>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                id={`group-template-${kind}-name-en`}
                label={t('groups.defaults.templates.form.nameEn')}
                value={nameEn}
                disabled={actions.pending}
                onChange={setNameEn}
              />
              <Field
                id={`group-template-${kind}-name-ar`}
                label={t('groups.defaults.templates.form.nameAr')}
                value={nameAr}
                dir="rtl"
                disabled={actions.pending}
                onChange={setNameAr}
              />
            </div>
            <Field
              id={`group-template-${kind}-code`}
              label={t('groups.defaults.templates.form.code')}
              value={code}
              disabled={actions.pending}
              onChange={(value) => setCode(value.toUpperCase())}
            />
          </DialogBody>
          <DialogFooter showCloseButton={false}>
            <Button type="button" variant="outline" onClick={onClose} disabled={actions.pending}>
              {t('groups.defaults.templates.form.cancel')}
            </Button>
            <Button type="submit" loading={actions.pending} disabled={actions.pending}>
              {t(
                item
                  ? 'groups.defaults.templates.form.saveEdit'
                  : 'groups.defaults.templates.form.saveCreate',
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function DeleteDialog({
  kind,
  item,
  actions,
  onClose,
}: {
  kind: TemplateKind
  item: TemplateItem | null
  actions: TemplateActions
  onClose: () => void
}) {
  const { t } = useTranslation()
  const remove = async () => {
    if (!item) return
    try {
      await actions.remove(item.id)
      onClose()
      toast.success(t('groups.defaults.templates.deleted'))
    } catch (cause) {
      toast.error(errorMessage(cause, t))
    }
  }
  return (
    <Dialog
      open={item !== null}
      onOpenChange={(open) => {
        if (!open && !actions.pending) onClose()
      }}
    >
      <DialogContent
        closeLabel={t('groups.defaults.templates.delete.cancel')}
        data-testid={`group-template-${kind}-delete-dialog`}
      >
        <DialogHeader>
          <DialogTitle>{t('groups.defaults.templates.delete.title')}</DialogTitle>
          <DialogDescription>
            {t('groups.defaults.templates.delete.body', { code: item?.code ?? '' })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter showCloseButton={false}>
          <Button type="button" variant="outline" onClick={onClose} disabled={actions.pending}>
            {t('groups.defaults.templates.delete.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            loading={actions.pending}
            disabled={actions.pending}
            onClick={() => void remove()}
          >
            {t('groups.defaults.templates.delete.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Field({
  id,
  label,
  value,
  dir,
  disabled,
  onChange,
}: {
  id: string
  label: string
  value: string
  dir?: 'rtl'
  disabled: boolean
  onChange: (value: string) => void
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        dir={dir}
        value={value}
        disabled={disabled}
        required
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}
function errorMessage(error: unknown, t: (key: string) => string): string {
  return formatErrorMessage(error, t('groups.defaults.errors.generic'))
}
